/// <amd-module name="Repos/Artifact/Discussion/ArtifactDiscussionUtil" />
import { CommentThreadContext } from "Repos/Common/Contracts/Git";
import { SpecialCommentPositions } from "Repos/Discussion/Common/DiscussionContracts";
import { DiscussionThreadModel, IDiscussionThreadModelProps, newThread } from "Repos/Discussion/DiscussionThread";
import { equals, newGuid } from "VSS/Core/Util/String";
import { ArtifactDiscussionThread, IArtifactDiscussionThreadModel } from "./ArtifactDiscussionContracts";
import { DiscussionThread } from "./Contracts/Discussion";

const DiscussionNamespace = "Microsoft.TeamFoundation.Discussion";
const ItemPath = DiscussionNamespace + ".ItemPath";
const SupportsMarkdown = DiscussionNamespace + ".SupportsMarkdown";
const UniqueID = DiscussionNamespace + ".UniqueID";
const DiscussionPosition = DiscussionNamespace + ".Position";
const EndColumn = DiscussionPosition + ".EndColumn";
const EndLine = DiscussionPosition + ".EndLine";
const PositionContext = DiscussionPosition + ".PositionContext";
const StartColumn = DiscussionPosition + ".StartColumn";
const StartLine = DiscussionPosition + ".StartLine";

const LeftBuffer = "LeftBuffer";
const RightBuffer = "RightBuffer";

const StringType = "System.String";
const IntType = "System.Int32";

function getPropertyValue<T>(properties: any, propertyName: string, defaultValue: T): T {
    if (properties) {
        const property = properties[propertyName];
        if (property) {
            return property.$value as T;
        }
    }

    return defaultValue;
}

export function isOriginalSide(threadModel: IArtifactDiscussionThreadModel): boolean | undefined {
    return threadModel.thread.threadContext && !!threadModel.thread.threadContext.leftFileStart;
}

export function getFilePath(threadModel: IArtifactDiscussionThreadModel): string | undefined {
    return threadModel.thread.threadContext ? threadModel.thread.threadContext.filePath : undefined;
}

export function filePathEquals(threadModel: IArtifactDiscussionThreadModel, itemPath: string): boolean {
    return equals(getFilePath(threadModel) || "", itemPath, true);
}

export function propertiesToThreadContext(thread: DiscussionThread): CommentThreadContext | undefined {
    const filePath = getPropertyValue<string>(thread.properties, ItemPath, "");
    if (filePath) {
        const endColumn = getPropertyValue<number>(thread.properties, EndColumn, 0);
        const endLine = getPropertyValue<number>(thread.properties, EndLine, 0);
        const positionContext = getPropertyValue<string>(thread.properties, PositionContext, "");
        const startColumn = getPropertyValue<number>(thread.properties, StartColumn, 0);
        const startLine = getPropertyValue<number>(thread.properties, StartLine, 0);

        const leftSide = equals(positionContext, LeftBuffer, true);
        if (leftSide) {
            return {
                filePath,
                leftFileEnd: { line: endLine, offset: endColumn },
                leftFileStart: { line: startLine, offset: startColumn }
            } as CommentThreadContext;
        }

        const rightSide = equals(positionContext, RightBuffer, true);
        if (rightSide) {
            return {
                filePath,
                rightFileEnd: { line: endLine, offset: endColumn },
                rightFileStart: { line: startLine, offset: startColumn }
            } as CommentThreadContext;
        }

        return { filePath } as CommentThreadContext;
    }
}

export function threadContextToProperties(threadContext?: CommentThreadContext): any {
    const properties: any = {};
    properties[SupportsMarkdown] = { type: IntType, value: 1 };
    properties[UniqueID] = { type: StringType, value: newGuid() };

    if (threadContext) {
        if (threadContext.filePath) {
            properties[ItemPath] = threadContext.filePath;
        }

        const { leftFileEnd, leftFileStart, rightFileEnd, rightFileStart } = threadContext;
        if (
            leftFileStart &&
            leftFileStart.line !== SpecialCommentPositions.File &&
            leftFileEnd &&
            leftFileEnd.line !== SpecialCommentPositions.File
        ) {
            properties[PositionContext] = { type: StringType, value: LeftBuffer };
            properties[EndColumn] = { type: IntType, value: leftFileEnd.offset };
            properties[EndLine] = { type: IntType, value: leftFileEnd.line };
            properties[StartColumn] = { type: IntType, value: leftFileStart.offset };
            properties[StartLine] = { type: IntType, value: leftFileStart.line };
        } else if (
            rightFileStart &&
            rightFileStart.line !== SpecialCommentPositions.File &&
            rightFileEnd &&
            rightFileEnd.line !== SpecialCommentPositions.File
        ) {
            properties[PositionContext] = { type: StringType, value: RightBuffer };
            properties[EndColumn] = { type: IntType, value: rightFileEnd.offset };
            properties[EndLine] = { type: IntType, value: rightFileEnd.line };
            properties[StartColumn] = { type: IntType, value: rightFileStart.offset };
            properties[StartLine] = { type: IntType, value: rightFileStart.line };
        }
    }

    return properties;
}

export function newArtifactThreadModel(props?: IDiscussionThreadModelProps): IArtifactDiscussionThreadModel {
    return new DiscussionThreadModel<ArtifactDiscussionThread>({ ...props, thread: newThread() as ArtifactDiscussionThread });
}
